import { AcquisitionChannel, deriveAcquisitionChannel, normalizeReferringHost } from '@tipi/shared'

export type AttributionParameters = {
    utm_source?: string
    utm_medium?: string
    utm_campaign?: string
    utm_term?: string
    utm_content?: string
    fbclid?: string
    gclid?: string
    gad_source?: string
    gad_campaignid?: string
    gbraid?: string
    referring_domain?: string
    initial_landing_path?: string
    acquisition_channel?: AcquisitionChannel
}

export type UTMParameters = AttributionParameters

const ATTRIBUTION_COOKIE_NAME = 'tipi_utm_attribution'
const PENDING_ATTRIBUTION_STORAGE_KEY = 'tipi_utm_attribution_pending'
const ATTRIBUTION_COOKIE_DAYS = 7

const ATTRIBUTION_STORAGE_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'fbclid',
    'gclid',
    'gad_source',
    'gad_campaignid',
    'gbraid',
    'referring_domain',
    'initial_landing_path',
] as const satisfies readonly (keyof AttributionParameters)[]

type AttributionStorageKey = (typeof ATTRIBUTION_STORAGE_KEYS)[number]

export function getParameterByName(name: string): string | null {
    const url = window.location.href
    // eslint-disable-next-line no-useless-escape
    name = name.replace(/[\[\]]/g, '\\$&')
    const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)')
    const results = regex.exec(url)
    if (!results) return null
    if (!results[2]) return ''
    return decodeURIComponent(results[2].replace(/\+/g, ' '))
}

function getCookieValue(name: string): string | undefined {
    if (typeof document === 'undefined') return undefined
    const cookies = document.cookie ? document.cookie.split('; ') : []
    for (const cookie of cookies) {
        const separatorIndex = cookie.indexOf('=')
        if (separatorIndex === -1) continue

        const key = cookie.slice(0, separatorIndex)
        if (key === name) {
            return cookie.slice(separatorIndex + 1)
        }
    }

    return undefined
}

function toStoragePayload(parameters: AttributionParameters): Partial<Record<AttributionStorageKey, string>> {
    const storagePayload: Partial<Record<AttributionStorageKey, string>> = {}
    for (const key of ATTRIBUTION_STORAGE_KEYS) {
        const value = parameters[key]
        if (typeof value === 'string' && value.length > 0) {
            storagePayload[key] = value
        }
    }
    return storagePayload
}

function pickAttributionParameters(value: unknown): AttributionParameters | undefined {
    if (typeof value !== 'object' || value === null) return undefined

    const parameters: AttributionParameters = {}
    for (const key of ATTRIBUTION_STORAGE_KEYS) {
        const field = (value as Record<string, unknown>)[key]
        if (typeof field === 'string' && field.length > 0) {
            parameters[key] = field
        }
    }

    return hasAnyAttributionSignal(parameters) ? parameters : undefined
}

function hasAnyAttributionSignal(parameters: AttributionParameters): boolean {
    return ATTRIBUTION_STORAGE_KEYS.some(key => Boolean(parameters[key]))
}

function setAttributionParametersCookie(parameters: AttributionParameters): void {
    if (typeof document === 'undefined' || typeof window === 'undefined') return
    const payload = encodeURIComponent(JSON.stringify(toStoragePayload(parameters)))
    const maxAgeInSeconds = ATTRIBUTION_COOKIE_DAYS * 24 * 60 * 60
    const secureCookieFlag = window.location.protocol === 'https:' ? '; Secure' : ''
    document.cookie = `${ATTRIBUTION_COOKIE_NAME}=${payload}; Max-Age=${maxAgeInSeconds}; Path=/; SameSite=Lax${secureCookieFlag}`
}

function getAttributionParametersFromCookie(): AttributionParameters | undefined {
    const cookieValue = getCookieValue(ATTRIBUTION_COOKIE_NAME)
    if (!cookieValue) return undefined
    try {
        return pickAttributionParameters(JSON.parse(decodeURIComponent(cookieValue)))
    } catch {
        return undefined
    }
}

function getPendingAttributionParametersFromSession(): AttributionParameters | undefined {
    if (typeof sessionStorage === 'undefined') return undefined
    try {
        const raw = sessionStorage.getItem(PENDING_ATTRIBUTION_STORAGE_KEY)
        if (!raw) return undefined
        return pickAttributionParameters(JSON.parse(raw))
    } catch {
        return undefined
    }
}

function setPendingAttributionParametersInSession(parameters: AttributionParameters): void {
    if (typeof sessionStorage === 'undefined') return
    try {
        sessionStorage.setItem(PENDING_ATTRIBUTION_STORAGE_KEY, JSON.stringify(toStoragePayload(parameters)))
    } catch {
        return
    }
}

function clearPendingAttributionParametersFromSession(): void {
    if (typeof sessionStorage === 'undefined') return
    try {
        sessionStorage.removeItem(PENDING_ATTRIBUTION_STORAGE_KEY)
    } catch {
        return
    }
}

function getQueryAttributionParameters(): AttributionParameters {
    if (typeof window === 'undefined') return {}

    const param = (name: string): string | undefined => {
        const value = getParameterByName(name)
        return value === null || value.length === 0 ? undefined : value
    }

    return {
        utm_source: param('utm_source'),
        utm_medium: param('utm_medium'),
        utm_campaign: param('utm_campaign'),
        utm_term: param('utm_term'),
        utm_content: param('utm_content'),
        fbclid: param('fbclid'),
        gclid: param('gclid'),
        gad_source: param('gad_source'),
        gad_campaignid: param('gad_campaignid'),
        gbraid: param('gbraid'),
    }
}

function captureLiveFirstTouchSignals(): AttributionParameters {
    const parameters: AttributionParameters = {
        ...getQueryAttributionParameters(),
    }

    if (typeof document !== 'undefined') {
        const referringDomain = normalizeReferringHost(document.referrer)
        if (referringDomain) parameters.referring_domain = referringDomain
    }

    if (typeof window !== 'undefined') {
        const path = window.location.pathname
        if (path.length > 0) parameters.initial_landing_path = path
    }

    return parameters
}

function withDerivedChannel(parameters: AttributionParameters): AttributionParameters {
    return {
        ...parameters,
        acquisition_channel: deriveAcquisitionChannel(parameters),
    }
}

export function captureFirstTouchAttribution(): void {
    if (getAttributionParametersFromCookie() !== undefined) return
    if (getPendingAttributionParametersFromSession() !== undefined) return

    const parameters = captureLiveFirstTouchSignals()
    if (!hasAnyAttributionSignal(parameters)) return

    setPendingAttributionParametersInSession(parameters)
}

export function persistAttributionToCookie(): void {
    if (getAttributionParametersFromCookie() !== undefined) return

    const parameters = getPendingAttributionParametersFromSession() ?? captureLiveFirstTouchSignals()
    if (!hasAnyAttributionSignal(parameters)) return

    setAttributionParametersCookie(parameters)
    clearPendingAttributionParametersFromSession()
}

/** @deprecated Prefer persistAttributionToCookie */
export function persistUTMParametersFromUrlToCookie(): void {
    persistAttributionToCookie()
}

export function getAttributionParameters(): AttributionParameters {
    const stored =
        getAttributionParametersFromCookie() ??
        getPendingAttributionParametersFromSession() ??
        captureLiveFirstTouchSignals()

    return withDerivedChannel(stored)
}

export function getUTMParameters(): AttributionParameters {
    return getAttributionParameters()
}
